// src/components/EditLoadHistory.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Clock,
  Loader2,
  MapPin,
  Package,
  Save,
  X,
  XCircle,
  Truck,
  User,
  Phone,
  Link as LinkIcon,
  Route,
  ScanText,
} from "lucide-react";
import "../styles/EditLoadHistory.css"; // novo ficheiro

export default function EditLoadHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get(`/load-history/${id}`);
        if (!ignore) setData(res.data);
      } catch (e) {
        if (!ignore) setError("Could not load edit view.");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id]);

  const load = data?.load || null;
  const initialPoints = useMemo(() => {
    const arr = Array.isArray(data?.pickupDeliveriesSnapshot)
      ? data.pickupDeliveriesSnapshot
      : Array.isArray(load?.pickupDeliveries)
      ? load.pickupDeliveries
      : [];
    return arr.map((p, idx) => ({
      key: `${p.id ?? "tmp"}-${idx}`,
      id: p.id,
      type: p.type,
      sequence: p.sequence ?? idx + 1,
      location: p.location ?? "",
      address: p.address ?? "",
      postalCode: p.postalCode ?? "",
      city: p.city ?? "",
      country: p.country ?? "",
      date: p.date ?? "",
      hour: p.hour ?? "",
      status: p.status ?? "",
      gMapsLink: p.gMapsLink ?? "",
      coordinates: stringifyCoords(p.coordinates),
      contactPhone: p.contactPhone ?? "",
      truckText: p?.truck?.plate || "",
      trailerText: p?.trailer?.trailerPlate || "",
      driverText: p?.driver?.name || "",
    }));
  }, [data, load]);

  const [points, setPoints] = useState([]);
  useEffect(() => { if (initialPoints.length) setPoints(initialPoints); }, [initialPoints.length]);

  function stringifyCoords(c) {
    if (!c) return "";
    if (typeof c === "string") return c;
    if (Array.isArray(c) && c.length >= 2) return `${c[0]}, ${c[1]}`;
    if (c?.lat != null && c?.lng != null) return `${c.lat}, ${c.lng}`;
    return String(c);
  }

  function updatePoint(idx, patch) {
    setPoints(prev => {
      const arr = [...prev];
      arr[idx] = { ...arr[idx], ...patch };
      return arr;
    });
  }

  async function handleSave() {
    if (!data?.id) return;
    setSaving(true);
    try {
      const snapshot = points.map(p => ({
        ...p,
      }));
      await axios.put(`/load-history/${data.id}`, {
        pickupDeliveriesSnapshot: snapshot
      });
      navigate(`/load-history/${data.id}`);
    } catch (e) {
      alert("Failed to save this history edit.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="edit-load-history-overlay">Loading…</div>;
  }
  if (error) {
    return (
      <div className="edit-load-history-overlay">
        <div className="edit-load-history-error">
          <XCircle /> {error}
          <button onClick={() => navigate(-1)} className="edit-load-history-btn-secondary">
            <ArrowLeft /> Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-load-history-root">
      <section className="edit-load-history-panel">
        {/* Header */}
        <header className="edit-load-history-header">
          <div className="edit-load-history-header-row">
            <button onClick={() => navigate(-1)} className="edit-load-history-close">
              <X className="edit-load-history-close-icon" />
              Close
            </button>
            <div className="edit-load-history-title">Edit Load History</div>
          </div>
        </header>

        {/* Content */}
        <main className="edit-load-history-content">
          <div className="edit-load-history-body">
            <div className="edit-load-history-timeline">
              <div className="edit-load-history-timeline-head">
                <Route className="edit-load-history-timeline-head-icon" />
                <span>Pickups & Deliveries</span>
              </div>

              <ol className="edit-load-history-steps">
                {points.map((p, idx) => (
                  <li key={p.key} className="edit-load-history-step">
                    <div className="edit-load-history-step-top">
                      <span className={`edit-load-history-tag ${p.type}`}>
                        {p.type.toUpperCase()} #{p.sequence}
                      </span>
                      <input
                        className="edit-load-history-input"
                        placeholder="Location"
                        value={p.location}
                        onChange={e => updatePoint(idx, { location: e.target.value })}
                      />
                    </div>

                    <div className="edit-load-history-fields">
                      <FieldInput label="Address" value={p.address} onChange={v => updatePoint(idx, { address: v })} />
                      <FieldInput label="Postal Code" value={p.postalCode} onChange={v => updatePoint(idx, { postalCode: v })} />
                      <FieldInput label="City" value={p.city} onChange={v => updatePoint(idx, { city: v })} />
                      <FieldInput label="Country" value={p.country} onChange={v => updatePoint(idx, { country: v })} />
                      <FieldInput label="Coordinates" value={p.coordinates} onChange={v => updatePoint(idx, { coordinates: v })} />
                      <FieldInput label="Maps Link" value={p.gMapsLink} onChange={v => updatePoint(idx, { gMapsLink: v })} />
                      <FieldInput label="Phone" value={p.contactPhone} onChange={v => updatePoint(idx, { contactPhone: v })} />
                    </div>

                    <div className="edit-load-history-fields">
                      <FieldInput label="Truck" value={p.truckText} onChange={v => updatePoint(idx, { truckText: v })} />
                      <FieldInput label="Trailer" value={p.trailerText} onChange={v => updatePoint(idx, { trailerText: v })} />
                      <FieldInput label="Driver" value={p.driverText} onChange={v => updatePoint(idx, { driverText: v })} />
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="edit-load-history-footer">
          <button
            className="edit-load-history-btn-secondary"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft /> Cancel
          </button>
          <button
            className="edit-load-history-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Loader2 className="edit-load-history-spin" /> : <Save />}
            Save
          </button>
        </footer>
      </section>
    </div>
  );
}

function FieldInput({ label, value, onChange }) {
  return (
    <div className="edit-load-history-fieldrow">
      <div className="edit-load-history-fieldlabel">{label}</div>
      <input
        className="edit-load-history-input"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={`Enter ${label}`}
      />
    </div>
  );
}